import React, { useContext } from "react";
import { Col, Row, Spinner } from "reactstrap";
import HeaderLogo from "src/components/HeaderLogo";
import { ErrorTypes, RequestStatus } from "src/lib/api/types";
import { useApi } from "src/lib/ApiProvider";

import { useNavigate } from "react-router-dom";
import { Footer } from "src/components/Footer";
import { isErrorResponse } from "src/lib/api/utils";
import { sanitizePortugueseVatNumber } from "src/lib/vatNumber";
import { ReceiptsContext } from "src/lib/receiptsContext";
import ErrorMessage from "./ErrorMessage";
import { ReceiptsForm } from "./ReceiptsForm";
import { ReceiptFormValues } from "./types";

export const RequestReceiptsForm = () => {
  const { receiptsStatus, searchReceipts } =
    useContext(ReceiptsContext);
  const [emailStatus, setEmailStatus] = React.useState<RequestStatus>("idle");
  const [errorType, setErrorType] = React.useState<ErrorTypes | null>(null);
  const [, setSearchParams] = React.useState<Record<string, string>>({});

  const { api, authApi } = useApi();
  const navigate = useNavigate();

  const handleRegisterEmail = React.useCallback(
    async (sessionToken: string, email: string) => {
      authApi
        .patch(
          `api/customer/v2/customers/me`,
          {
            email,
          },
          { headers: { authorization: `Bearer ${sessionToken}` } } // token has to be explicitely passed as it can't be taken from urlParams
        )
        .then((res) => {
          if (res.status === 200) {
            setEmailStatus("success");
          }
        })
        .catch((err) => {
          setEmailStatus("error");
          console.error(err);
        });
    },
    [authApi]
  );
  const handleRegisterVatNumber = React.useCallback(
    async (sessionToken: string, vatNumber: string) => {
      // Store the VAT/NIF on the OASIS customer's `taxId` field so downstream
      // fiscal-compliance integrations (InvoiceExpress) can read it.
      // This is intentionally a separate PATCH from handleRegisterEmail so that
      // a failure in one (e.g. email already taken) never prevents the other
      // from succeeding — independent error handling is more resilient here.
      // Fire-and-forget: a VAT registration failure must not block the shopper
      // from seeing their receipts.
      authApi
        .patch(
          `api/customer/v2/customers/me`,
          { taxId: sanitizePortugueseVatNumber(vatNumber) },
          { headers: { authorization: `Bearer ${sessionToken}` } } // token has to be explicitely passed as it can't be taken from urlParams
        )
        .catch((err) => {
          console.error(err);
        });
    },
    [authApi]
  );
  const handleSubmit = React.useCallback(
    async (values: ReceiptFormValues) => {
      setErrorType(null);
      try {
        const { vatNumber, ...searchValues } = values;
        const params = searchValues as unknown as Record<string, string>;
        setSearchParams(params);

        const receiptsData = await searchReceipts(api, params);

        // Email registration is handled after successful receipt search
        if (values.email) {
          // Note: If registration will fail, this won't be reflected in the interface as user
          // will be redirected to receipts list. While working on related issue I decided not to
          // refactor this part as I believe it's better to show the receipts than to stop whole
          // customer from retrieving the receipt. See OASIS-18820 for improvement ticket.
          if (receiptsData && receiptsData.length > 0) {
            const sessionToken = receiptsData[0].sessionToken;
            const email = values.email!;
            handleRegisterEmail(sessionToken, email);
          } else {
            setEmailStatus("error");
          }
        }

        // VAT/NIF registration is handled after successful receipt search, like
        // email. It's optional and best-effort, so it never blocks navigation.
        if (values.vatNumber && values.vatNumber.trim().length > 0) {
          if (receiptsData && receiptsData.length > 0) {
            handleRegisterVatNumber(
              receiptsData[0].sessionToken,
              values.vatNumber
            );
          }
        }

        navigate("/list", { replace: false, state: { searchParams: params } });
      } catch (err) {
        if (isErrorResponse(err)) {
          setErrorType(err.response.data.type);
        } else {
          setErrorType(ErrorTypes.UNRECOGNIZED_ERR);
        }
      }
    },
    [api, handleRegisterEmail, handleRegisterVatNumber, searchReceipts, navigate]
  );
  
  
  return (
    <div
      style={{
        padding: "16px",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <HeaderLogo />
      <Col>
        <ReceiptsForm onSubmit={handleSubmit} emailStatus={emailStatus} />
        {receiptsStatus === "loading" && (
          <Row className="justify-content-center">
            <Spinner />
          </Row>
        )}
      </Col>
      {receiptsStatus === "error" && errorType !== null && (
        <Col>
          <Row className="justify-content-center">
            <ErrorMessage errorType={errorType} />
          </Row>
        </Col>
      )}
      <Footer />
    </div>
  );
};
